/*
 * WebCRD
 * Web to print solution that automates ordering, fulfillment, job ticketing, production management and chargebacks across corporate print centers.
 * Copyright 1999-2026 Rochester Software Associates (service@rocsoft.com)
 */

import { useEffect } from 'react';

/**
 * Mimics how the backend trims order names to length by bytes instead of chars.
 * This lets us prevent a desync compared to using maxLength since maxLength counts chars, not bytes.
 *
 * @param {string} str - the string to trim
 * @param {number} maxBytes - the number of bytes to trim to (e.g. 80 bytes for order name input)
 * @returns {string} the trimmed string
 */
const trimToMaxBytes = (str, maxBytes) => {
    const encoder = new TextEncoder();
    let bytes = encoder.encode(str);

    if (bytes.length <= maxBytes) {
        return str;
    }

    // binary search
    let low = 0;
    let high = str.length;
    while (low < high) {
        const mid = (low + high) >>> 1;
        bytes = encoder.encode(str.slice(0, mid));
        if (bytes.length <= maxBytes) {
            low = mid + 1;
        } else {
            high = mid;
        }
    }

    return str.slice(0, low - 1);
};

/**
 * Hook to enforce a utf-8 byte limit on the visible text.
 *
 * @param {React.RefObject<HTMLInputElement|HTMLTextAreaElement>} inputRef
 * @param {number} maxBytes
 */
const useByteLimitOnRef = (inputRef, maxBytes) => {
    useEffect(() => {
        // early return to obey rules of hooks
        if (!maxBytes) {
            return;
        }

        const inputElement = inputRef?.current;
        if (!inputElement) {
            return;
        }

        const onInput = () => {
            // get current value from DOM
            const cur = inputElement.value;
            const trimmed = trimToMaxBytes(cur, maxBytes);
            if (trimmed === cur) {
                return;
            }

            // remember caret so we can place it sensibly after trimming
            const selStart = inputElement.selectionStart ?? trimmed.length;
            const newPos = Math.min(selStart, trimmed.length);

            // set the DOM value to the trimmed string
            inputElement.value = trimmed;

            // put caret back
            inputElement.setSelectionRange(newPos, newPos);

            // dispatch an input event so the component's onInput handler runs with the trimmed value
            inputElement.dispatchEvent(new Event('input', { bubbles: true, cancelable: true }));
        };

        inputElement.addEventListener('input', onInput);
        return () => inputElement.removeEventListener('input', onInput);
    }, [inputRef, maxBytes]);
};

export default useByteLimitOnRef;